import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Edit, Save, X, Lock as LockIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function TemplateManagement() {
  const [departmentTemplates, setDepartmentTemplates] = useState([]);
  const [kraTemplates, setKraTemplates] = useState([]);
  const [kpiTemplates, setKpiTemplates] = useState([]);
  const [selectedDeptTemplate, setSelectedDeptTemplate] = useState(null);
  const [selectedKraTemplate, setSelectedKraTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Default School Structure
  const [defaultSchoolTenantId, setDefaultSchoolTenantId] = useState(null);
  const [defaultDepartments, setDefaultDepartments] = useState([]);
  const [defaultKras, setDefaultKras] = useState([]);
  const [defaultKpis, setDefaultKpis] = useState([]);
  const [activeTab, setActiveTab] = useState('default-school'); // 'default-school' or 'custom-templates'

  // Dialog states
  const [showDeptDialog, setShowDeptDialog] = useState(false);
  const [showKraDialog, setShowKraDialog] = useState(false);
  const [showKpiDialog, setShowKpiDialog] = useState(false);
  const [showDefaultDeptDialog, setShowDefaultDeptDialog] = useState(false);
  const [showDefaultKraDialog, setShowDefaultKraDialog] = useState(false);
  const [showDefaultKpiDialog, setShowDefaultKpiDialog] = useState(false);

  // Form states
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptHead, setNewDeptHead] = useState('');
  const [selectedDefaultDept, setSelectedDefaultDept] = useState(null);
  const [selectedDefaultKra, setSelectedDefaultKra] = useState(null);
  const [newKra, setNewKra] = useState({ title: '', description: '' });
  const [newKpi, setNewKpi] = useState({
    title: '',
    description: '',
    weightage: '',
    sop: ''
  });

  useEffect(() => {
    fetchDefaultSchool();
    fetchAllTemplates();
  }, []);

  const fetchDefaultSchool = async () => {
    try {
      setLoading(true);
      // Fetch all tenants and find Default School
      const tenantsRes = await axios.get(`${API}/super-admin/tenants`);
      const defaultTenant = tenantsRes.data.find(t => t.school_name === 'Default School');
      
      if (defaultTenant) {
        setDefaultSchoolTenantId(defaultTenant.id);
        
        // Fetch Default School's departments
        const deptRes = await axios.get(`${API}/departments?tenant_id=${defaultTenant.id}`);
        
        // Fetch KRAs and KPIs for each department
        const allKras = [];
        const allKpis = [];
        
        for (const dept of deptRes.data) {
          try {
            const krasRes = await axios.get(`${API}/kras/${dept.id}?tenant_id=${defaultTenant.id}`);
            allKras.push(...krasRes.data);
            
            const kpisRes = await axios.get(`${API}/kpis/department/${dept.id}?tenant_id=${defaultTenant.id}`);
            allKpis.push(...kpisRes.data);
          } catch (error) {
            console.error(`Error fetching KRAs/KPIs for ${dept.name}:`, error);
          }
        }
        
        setDefaultDepartments(deptRes.data);
        setDefaultKras(allKras);
        setDefaultKpis(allKpis);
      }
    } catch (error) {
      console.error('Error fetching Default School:', error);
      toast.error('Failed to load Default School structure');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTemplates = async () => {
    try {
      setLoading(true);
      const [deptRes, kraRes, kpiRes] = await Promise.all([
        axios.get(`${API}/super-admin/templates/departments`),
        axios.get(`${API}/super-admin/templates/kras`),
        axios.get(`${API}/super-admin/templates/kpis`)
      ]);
      
      setDepartmentTemplates(deptRes.data);
      setKraTemplates(kraRes.data);
      setKpiTemplates(kpiRes.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPredefined = async (deptId) => {
    if (!window.confirm('Mark this department and all its KRAs/KPIs as predefined? This will make them available to all schools.')) {
      return;
    }

    try {
      // Update department to mark as predefined
      await axios.put(`${API}/departments/${deptId}?tenant_id=${defaultSchoolTenantId}`, {
        is_predefined: true
      });
      
      // Update all KRAs for this department
      const deptKras = defaultKras.filter(k => k.department_id === deptId);
      for (const kra of deptKras) {
        await axios.put(`${API}/kras/${kra.id}?tenant_id=${defaultSchoolTenantId}`, {
          is_predefined: true,
          department_id: kra.department_id,
          title: kra.title,
          description: kra.description
        });
        
        // Update all KPIs for this KRA
        const kraKpis = defaultKpis.filter(k => k.kra_id === kra.id);
        for (const kpi of kraKpis) {
          await axios.put(`${API}/kpis/${kpi.id}?tenant_id=${defaultSchoolTenantId}`, {
            is_predefined: true,
            kra_id: kpi.kra_id,
            department_id: kpi.department_id,
            title: kpi.title,
            description: kpi.description,
            weightage: kpi.weightage,
            sop: kpi.sop
          });
        }
      }
      
      toast.success('Successfully marked as predefined template');
      fetchDefaultSchool();
    } catch (error) {
      console.error('Error marking as predefined:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as predefined');
    }
  };

  const handleCopyToNewSchools = async (deptId) => {
    if (!window.confirm('Copy this department structure to all active schools? This will add the department, KRAs, and KPIs to all schools.')) {
      return;
    }

    try {
      // First mark as predefined
      await handleMarkAsPredefined(deptId);
      
      // Get all active schools except Default School
      const tenantsRes = await axios.get(`${API}/super-admin/tenants`);
      const activeSchools = tenantsRes.data.filter(
        t => t.status === 'active' && t.id !== defaultSchoolTenantId
      );
      
      const dept = defaultDepartments.find(d => d.id === deptId);
      const deptKras = defaultKras.filter(k => k.department_id === deptId);
      
      for (const school of activeSchools) {
        // Check if department already exists
        const existingDepts = await axios.get(`${API}/departments?tenant_id=${school.id}`);
        const deptExists = existingDepts.data.find(d => d.name === dept.name);
        
        if (!deptExists) {
          // Create department
          const newDeptRes = await axios.post(
            `${API}/departments?name=${encodeURIComponent(dept.name)}&tenant_id=${school.id}&is_predefined=true&head_name=${encodeURIComponent(dept.head_name || '')}`
          );
          const newDeptId = newDeptRes.data.id;
          
          // Create KRAs and KPIs
          for (const kra of deptKras) {
            const newKraRes = await axios.post(
              `${API}/kras?tenant_id=${school.id}&is_predefined=true`,
              {
                department_id: newDeptId,
                title: kra.title,
                description: kra.description
              }
            );
            const newKraId = newKraRes.data.id;
            
            // Create KPIs
            const kraKpis = defaultKpis.filter(k => k.kra_id === kra.id);
            for (const kpi of kraKpis) {
              await axios.post(
                `${API}/kpis?tenant_id=${school.id}&is_predefined=true`,
                {
                  kra_id: newKraId,
                  department_id: newDeptId,
                  title: kpi.title,
                  description: kpi.description,
                  weightage: kpi.weightage,
                  sop: kpi.sop
                }
              );
            }
          }
        }
      }
      
      toast.success(`Successfully copied to ${activeSchools.length} schools`);
    } catch (error) {
      console.error('Error copying to schools:', error);
      toast.error('Failed to copy to all schools');
    }
  };

  // Default School CRUD Operations
  const handleCreateDefaultDept = async () => {
    if (!newDeptName.trim()) {
      toast.error('Department name is required');
      return;
    }

    try {
      await axios.post(
        `${API}/departments?name=${encodeURIComponent(newDeptName)}&tenant_id=${defaultSchoolTenantId}&head_name=${encodeURIComponent(newDeptHead || '')}`
      );
      toast.success('Department added to Default School');
      setNewDeptName('');
      setNewDeptHead('');
      setShowDefaultDeptDialog(false);
      fetchDefaultSchool();
    } catch (error) {
      console.error('Error creating department:', error);
      toast.error(error.response?.data?.detail || 'Failed to create department');
    }
  };

  const handleDeleteDefaultDept = async (deptId, deptName) => {
    if (!window.confirm(`Delete "${deptName}" and all its KRAs/KPIs from Default School?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/departments/${deptId}?tenant_id=${defaultSchoolTenantId}`);
      toast.success('Department deleted successfully');
      fetchDefaultSchool();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete department');
    }
  };

  const handleCreateDefaultKra = async () => {
    if (!newKra.title.trim()) {
      toast.error('KRA title is required');
      return;
    }

    try {
      await axios.post(`${API}/kras?tenant_id=${defaultSchoolTenantId}`, {
        department_id: selectedDefaultDept,
        title: newKra.title,
        description: newKra.description
      });
      toast.success('KRA added successfully');
      setNewKra({ title: '', description: '' });
      setShowDefaultKraDialog(false);
      fetchDefaultSchool();
    } catch (error) {
      console.error('Error creating KRA:', error);
      toast.error(error.response?.data?.detail || 'Failed to create KRA');
    }
  };

  const handleDeleteDefaultKra = async (kraId, kraTitle) => {
    if (!window.confirm(`Delete KRA "${kraTitle}" and all its KPIs?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/kras/${kraId}?tenant_id=${defaultSchoolTenantId}`);
      toast.success('KRA deleted successfully');
      fetchDefaultSchool();
    } catch (error) {
      console.error('Error deleting KRA:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete KRA');
    }
  };

  const handleCreateDefaultKpi = async () => {
    if (!newKpi.title.trim() || !newKpi.weightage) {
      toast.error('KPI title and weightage are required');
      return;
    }

    try {
      await axios.post(`${API}/kpis?tenant_id=${defaultSchoolTenantId}`, {
        kra_id: selectedDefaultKra,
        department_id: selectedDefaultDept,
        title: newKpi.title,
        description: newKpi.description,
        weightage: parseFloat(newKpi.weightage),
        sop: newKpi.sop
      });
      toast.success('KPI added successfully');
      setNewKpi({ title: '', description: '', weightage: '', sop: '' });
      setShowDefaultKpiDialog(false);
      fetchDefaultSchool();
    } catch (error) {
      console.error('Error creating KPI:', error);
      toast.error(error.response?.data?.detail || 'Failed to create KPI');
    }
  };

  const handleDeleteDefaultKpi = async (kpiId, kpiTitle) => {
    if (!window.confirm(`Delete KPI "${kpiTitle}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/kpis/${kpiId}?tenant_id=${defaultSchoolTenantId}`);
      toast.success('KPI deleted successfully');
      fetchDefaultSchool();
    } catch (error) {
      console.error('Error deleting KPI:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete KPI');
    }
  };

  const handleCreateDepartmentTemplate = async () => {
    if (!newDeptName.trim()) {
      toast.error('Department name is required');
      return;
    }

    try {
      await axios.post(`${API}/super-admin/templates/departments`, {
        department_name: newDeptName
      });
      toast.success('Department template created successfully');
      setNewDeptName('');
      setShowDeptDialog(false);
      fetchAllTemplates();
    } catch (error) {
      console.error('Error creating department template:', error);
      toast.error(error.response?.data?.detail || 'Failed to create department template');
    }
  };

  const handleCreateKraTemplate = async () => {
    if (!selectedDeptTemplate) {
      toast.error('Please select a department template first');
      return;
    }
    if (!newKra.title.trim()) {
      toast.error('KRA title is required');
      return;
    }

    try {
      await axios.post(`${API}/super-admin/templates/kras`, {
        department_template_id: selectedDeptTemplate,
        kra_title: newKra.title,
        kra_description: newKra.description
      });
      toast.success('KRA template created successfully');
      setNewKra({ title: '', description: '' });
      setShowKraDialog(false);
      fetchAllTemplates();
    } catch (error) {
      console.error('Error creating KRA template:', error);
      toast.error(error.response?.data?.detail || 'Failed to create KRA template');
    }
  };

  const handleCreateKpiTemplate = async () => {
    if (!selectedKraTemplate) {
      toast.error('Please select a KRA template first');
      return;
    }
    if (!newKpi.title.trim() || !newKpi.weightage) {
      toast.error('KPI title and weightage are required');
      return;
    }

    try {
      await axios.post(`${API}/super-admin/templates/kpis`, {
        kra_template_id: selectedKraTemplate,
        kpi_title: newKpi.title,
        kpi_description: newKpi.description,
        kpi_weightage: parseFloat(newKpi.weightage),
        kpi_sop: newKpi.sop
      });
      toast.success('KPI template created successfully');
      setNewKpi({ title: '', description: '', weightage: '', sop: '' });
      setShowKpiDialog(false);
      fetchAllTemplates();
    } catch (error) {
      console.error('Error creating KPI template:', error);
      toast.error(error.response?.data?.detail || 'Failed to create KPI template');
    }
  };

  // Get KRAs for selected department
  const getKrasForDepartment = (deptId) => {
    return kraTemplates.filter(kra => kra.department_template_id === deptId);
  };

  // Get KPIs for selected KRA
  const getKpisForKra = (kraId) => {
    return kpiTemplates.filter(kpi => kpi.kra_template_id === kraId);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-slate-500">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-900">Template Management</h2>
      </div>

      {/* Tabs for Default School vs Custom Templates */}
      <div className="flex space-x-2 border-b">
        <button
          onClick={() => setActiveTab('default-school')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'default-school'
              ? 'border-b-2 border-indigo-600 text-indigo-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Default School Structure
        </button>
        <button
          onClick={() => setActiveTab('custom-templates')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'custom-templates'
              ? 'border-b-2 border-indigo-600 text-indigo-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Custom Templates
        </button>
      </div>

      {/* Default School Structure Tab */}
      {activeTab === 'default-school' && (
        <div className="space-y-4">
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Default School Template Structure</h3>
                <p className="text-sm text-slate-600">
                  Manage the complete structure for your Default School. Add departments, KRAs, and KPIs here. 
                  You can mark them as predefined templates and copy them to all active schools.
                </p>
              </div>
              <Button
                onClick={() => setShowDefaultDeptDialog(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Department
              </Button>
            </div>
          </Card>

          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading Default School structure...</div>
          ) : defaultDepartments.length === 0 ? (
            <Card className="p-12 text-center">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Departments Found</h3>
              <p className="text-slate-600">
                The Default School doesn't have any departments yet. Create departments in the Default School first.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {defaultDepartments.map((dept) => {
                const deptKras = defaultKras.filter(k => k.department_id === dept.id);
                const totalKpis = deptKras.reduce((sum, kra) => {
                  return sum + defaultKpis.filter(kpi => kpi.kra_id === kra.id).length;
                }, 0);
                const totalWeightage = defaultKpis
                  .filter(kpi => deptKras.find(kra => kra.id === kpi.kra_id))
                  .reduce((sum, kpi) => sum + kpi.kpi_weightage, 0);
                const isPredefined = dept.is_predefined || false;

                return (
                  <Card key={dept.id} className={`p-6 ${isPredefined ? 'border-2 border-green-300 bg-green-50/30' : 'border-2 border-slate-200'}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold text-slate-900">{dept.name}</h3>
                          {isPredefined && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-full">
                              <LockIcon className="w-3 h-3 text-green-600" />
                              <span className="text-xs font-medium text-green-700">Predefined</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-slate-600 mt-2">
                          <span>{deptKras.length} KRAs</span>
                          <span>•</span>
                          <span>{totalKpis} KPIs</span>
                          <span>•</span>
                          <span>Total Weightage: {totalWeightage.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => {
                            setSelectedDefaultDept(dept.id);
                            setShowDefaultKraDialog(true);
                          }}
                          size="sm"
                          variant="outline"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add KRA
                        </Button>
                        {!isPredefined && (
                          <Button
                            onClick={() => handleMarkAsPredefined(dept.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <LockIcon className="w-4 h-4 mr-1" />
                            Mark Predefined
                          </Button>
                        )}
                        <Button
                          onClick={() => handleCopyToNewSchools(dept.id)}
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Copy to Schools
                        </Button>
                        <Button
                          onClick={() => handleDeleteDefaultDept(dept.id, dept.name)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* KRAs for this department */}
                    {deptKras.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {deptKras.map((kra) => {
                          const kraKpis = defaultKpis.filter(kpi => kpi.kra_id === kra.id);
                          const isKraPredefined = kra.is_predefined || false;
                          
                          return (
                            <Card key={kra.id} className={`p-4 ${isKraPredefined ? 'bg-green-50/50' : 'bg-slate-50'}`}>
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-slate-900">{kra.title}</h4>
                                    {isKraPredefined && (
                                      <LockIcon className="w-3 h-3 text-green-600" />
                                    )}
                                  </div>
                                  {kra.description && (
                                    <p className="text-sm text-slate-600 mt-1 italic">{kra.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Button
                                    onClick={() => {
                                      setSelectedDefaultDept(dept.id);
                                      setSelectedDefaultKra(kra.id);
                                      setShowDefaultKpiDialog(true);
                                    }}
                                    size="sm"
                                    variant="outline"
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add KPI
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteDefaultKra(kra.id, kra.title)}
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>

                              {/* KPIs for this KRA */}
                              {kraKpis.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {kraKpis.map((kpi) => {
                                    const isKpiPredefined = kpi.is_predefined || false;
                                    
                                    return (
                                      <div key={kpi.id} className={`flex items-start space-x-3 p-3 rounded border ${isKpiPredefined ? 'bg-white border-green-200' : 'bg-white'}`}>
                                        <div className="flex-1">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <h5 className="font-medium text-slate-900">{kpi.title}</h5>
                                              {isKpiPredefined && (
                                                <LockIcon className="w-3 h-3 text-green-600" />
                                              )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm font-semibold text-indigo-600">
                                                Weightage: {kpi.weightage}%
                                              </span>
                                              <Button
                                                onClick={() => handleDeleteDefaultKpi(kpi.id, kpi.title)}
                                                size="sm"
                                                variant="outline"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </div>
                                          </div>
                                          {kpi.description && (
                                            <p className="text-sm text-slate-600 mt-1">{kpi.description}</p>
                                          )}
                                          {kpi.sop && (
                                            <details className="mt-2">
                                              <summary className="text-sm text-indigo-600 cursor-pointer">
                                                View SOP
                                              </summary>
                                              <p className="text-sm text-slate-600 mt-2 pl-4 border-l-2 border-indigo-200 whitespace-pre-wrap">
                                                {kpi.sop}
                                              </p>
                                            </details>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Custom Templates Tab */}
      {activeTab === 'custom-templates' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setShowDeptDialog(true)}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Department Template
            </Button>
          </div>

      {/* Department Templates List */}
      {departmentTemplates.length === 0 ? (
        <Card className="p-12 text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Department Templates</h3>
          <p className="text-slate-600 mb-4">
            Create department templates that will be available to all schools
          </p>
          <Button
            onClick={() => setShowDeptDialog(true)}
            variant="outline"
          >
            Create First Department Template
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {departmentTemplates.map((dept) => {
            const deptKras = getKrasForDepartment(dept.id);
            const totalKpis = deptKras.reduce((sum, kra) => sum + getKpisForKra(kra.id).length, 0);
            const totalWeightage = kpiTemplates
              .filter(kpi => deptKras.find(kra => kra.id === kpi.kra_template_id))
              .reduce((sum, kpi) => sum + kpi.kpi_weightage, 0);

            return (
              <Card key={dept.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{dept.department_name}</h3>
                    <div className="flex items-center space-x-4 text-sm text-slate-600 mt-2">
                      <span>{deptKras.length} KRAs</span>
                      <span>•</span>
                      <span>{totalKpis} KPIs</span>
                      <span>•</span>
                      <span>Total Weightage: {totalWeightage}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => {
                        setSelectedDeptTemplate(dept.id);
                        setShowKraDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add KRA
                    </Button>
                  </div>
                </div>

                {/* KRAs for this department */}
                {deptKras.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {deptKras.map((kra) => {
                      const kraKpis = getKpisForKra(kra.id);
                      return (
                        <Card key={kra.id} className="p-4 bg-slate-50">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h4 className="font-semibold text-slate-900">{kra.kra_title}</h4>
                              {kra.kra_description && (
                                <p className="text-sm text-slate-600 mt-1 italic">{kra.kra_description}</p>
                              )}
                            </div>
                            <Button
                              onClick={() => {
                                setSelectedKraTemplate(kra.id);
                                setShowKpiDialog(true);
                              }}
                              size="sm"
                              variant="outline"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add KPI
                            </Button>
                          </div>

                          {/* KPIs for this KRA */}
                          {kraKpis.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {kraKpis.map((kpi) => (
                                <div key={kpi.id} className="flex items-start space-x-3 p-3 bg-white rounded border">
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <h5 className="font-medium text-slate-900">{kpi.kpi_title}</h5>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-indigo-600">
                                          Weightage: {kpi.kpi_weightage}
                                        </span>
                                        <Button
                                          onClick={() => handleDeleteDefaultKpi(kpi.id, kpi.kpi_title)}
                                          size="sm"
                                          variant="outline"
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                    {kpi.kpi_description && (
                                      <p className="text-sm text-slate-600 mt-1">{kpi.kpi_description}</p>
                                    )}
                                    {kpi.kpi_sop && (
                                      <details className="mt-2">
                                        <summary className="text-sm text-indigo-600 cursor-pointer">
                                          View SOP
                                        </summary>
                                        <p className="text-sm text-slate-600 mt-2 pl-4 border-l-2 border-indigo-200">
                                          {kpi.kpi_sop}
                                        </p>
                                      </details>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
        </div>
      )}

      {/* Add Default Department Dialog */}
      <Dialog open={showDefaultDeptDialog} onOpenChange={setShowDefaultDeptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Department to Default School</DialogTitle>
            <DialogDescription>
              Add a new department to the Default School structure
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="default-dept-name">Department Name *</Label>
              <Input
                id="default-dept-name"
                placeholder="e.g., Finance, HR, IT"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="default-dept-head">Department Head</Label>
              <Input
                id="default-dept-head"
                placeholder="e.g., John Doe"
                value={newDeptHead}
                onChange={(e) => setNewDeptHead(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDefaultDeptDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateDefaultDept}>
              Add Department
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Default KRA Dialog */}
      <Dialog open={showDefaultKraDialog} onOpenChange={setShowDefaultKraDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add KRA to Default School</DialogTitle>
            <DialogDescription>
              Add a new KRA to{' '}
              {defaultDepartments.find(d => d.id === selectedDefaultDept)?.name || 'the selected department'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="default-kra-title">KRA Title *</Label>
              <Input
                id="default-kra-title"
                placeholder="e.g., Financial Management"
                value={newKra.title}
                onChange={(e) => setNewKra({ ...newKra, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="default-kra-desc">KRA Description</Label>
              <Textarea
                id="default-kra-desc"
                placeholder="Describe what this KRA measures..."
                value={newKra.description}
                onChange={(e) => setNewKra({ ...newKra, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDefaultKraDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateDefaultKra}>
              Add KRA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Default KPI Dialog */}
      <Dialog open={showDefaultKpiDialog} onOpenChange={setShowDefaultKpiDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add KPI to Default School</DialogTitle>
            <DialogDescription>
              Add a new KPI to{' '}
              {defaultKras.find(k => k.id === selectedDefaultKra)?.title || 'the selected KRA'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
            <div>
              <Label htmlFor="default-kpi-title">KPI Title *</Label>
              <Input
                id="default-kpi-title"
                placeholder="e.g., Budget Adherence"
                value={newKpi.title}
                onChange={(e) => setNewKpi({ ...newKpi, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="default-kpi-desc">KPI Description</Label>
              <Textarea
                id="default-kpi-desc"
                placeholder="Describe what this KPI measures..."
                value={newKpi.description}
                onChange={(e) => setNewKpi({ ...newKpi, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="default-kpi-weightage">Weightage (%) *</Label>
              <Input
                id="default-kpi-weightage"
                type="number"
                placeholder="e.g., 25"
                value={newKpi.weightage}
                onChange={(e) => setNewKpi({ ...newKpi, weightage: e.target.value })}
                min="0"
                max="100"
                step="0.01"
              />
            </div>
            <div>
              <Label htmlFor="default-kpi-sop">Standard Operating Procedure (SOP)</Label>
              <Textarea
                id="default-kpi-sop"
                placeholder="Enter the step-by-step procedure for this KPI..."
                value={newKpi.sop}
                onChange={(e) => setNewKpi({ ...newKpi, sop: e.target.value })}
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDefaultKpiDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateDefaultKpi}>
              Add KPI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Department Template Dialog */}
      <Dialog open={showDeptDialog} onOpenChange={setShowDeptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Department Template</DialogTitle>
            <DialogDescription>
              Create a predefined department template that will be available to all schools
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="dept-name">Department Name *</Label>
              <Input
                id="dept-name"
                placeholder="e.g., Finance, HR, IT"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeptDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateDepartmentTemplate}>
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add KRA Template Dialog */}
      <Dialog open={showKraDialog} onOpenChange={setShowKraDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add KRA Template</DialogTitle>
            <DialogDescription>
              Create a predefined KRA template for{' '}
              {departmentTemplates.find(d => d.id === selectedDeptTemplate)?.department_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="kra-title">KRA Title *</Label>
              <Input
                id="kra-title"
                placeholder="e.g., Financial Management"
                value={newKra.title}
                onChange={(e) => setNewKra({ ...newKra, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="kra-desc">KRA Description</Label>
              <Textarea
                id="kra-desc"
                placeholder="Describe what this KRA measures..."
                value={newKra.description}
                onChange={(e) => setNewKra({ ...newKra, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKraDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKraTemplate}>
              Create KRA Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add KPI Template Dialog */}
      <Dialog open={showKpiDialog} onOpenChange={setShowKpiDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add KPI Template</DialogTitle>
            <DialogDescription>
              Create a predefined KPI template for{' '}
              {kraTemplates.find(k => k.id === selectedKraTemplate)?.kra_title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
            <div>
              <Label htmlFor="kpi-title">KPI Title *</Label>
              <Input
                id="kpi-title"
                placeholder="e.g., Budget Adherence"
                value={newKpi.title}
                onChange={(e) => setNewKpi({ ...newKpi, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="kpi-desc">KPI Description</Label>
              <Textarea
                id="kpi-desc"
                placeholder="Describe what this KPI measures..."
                value={newKpi.description}
                onChange={(e) => setNewKpi({ ...newKpi, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="kpi-weightage">Weightage (%) *</Label>
              <Input
                id="kpi-weightage"
                type="number"
                placeholder="e.g., 25"
                value={newKpi.weightage}
                onChange={(e) => setNewKpi({ ...newKpi, weightage: e.target.value })}
                min="0"
                max="100"
                step="0.01"
              />
            </div>
            <div>
              <Label htmlFor="kpi-sop">Standard Operating Procedure (SOP)</Label>
              <Textarea
                id="kpi-sop"
                placeholder="Enter the step-by-step procedure for this KPI..."
                value={newKpi.sop}
                onChange={(e) => setNewKpi({ ...newKpi, sop: e.target.value })}
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKpiDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKpiTemplate}>
              Create KPI Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
